import React from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Loader2 } from 'lucide-react';

/**
 * Modal de confirmación reutilizable para acciones destructivas o relevantes.
 * Sigue el patrón visual de Collaborators.js (modal de eliminar miembro).
 *
 * Props:
 *   - isOpen: boolean
 *   - onClose: () => void
 *   - onConfirm: () => void | Promise<void>
 *   - title: string
 *   - description: ReactNode (puede llevar JSX para resaltar palabras)
 *   - warning: string | ReactNode | null (advertencia opcional en card roja)
 *   - confirmLabel: string (default "Confirmar")
 *   - confirmIcon: ComponentType (ícono lucide opcional al lado del label)
 *   - cancelLabel: string (default "Cancelar")
 *   - variant: 'danger' | 'warning' | 'primary' (default 'danger')
 *   - loading: boolean (deshabilita y muestra spinner en confirmar)
 */

const VARIANT_STYLES = {
  danger: {
    iconBg: 'bg-red-50 dark:bg-red-900/30 border border-red-500/30',
    iconColor: 'text-red-700 dark:text-red-400',
    confirmBtn: 'bg-red-600 hover:bg-red-700 text-content-primary',
  },
  warning: {
    iconBg: 'bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-500/30',
    iconColor: 'text-yellow-700 dark:text-yellow-400',
    confirmBtn: 'bg-yellow-600 hover:bg-yellow-700 text-black',
  },
  primary: {
    iconBg: 'bg-kumia-green/15 border border-kumia-green/30',
    iconColor: 'text-kumia-green',
    confirmBtn: 'bg-kumia-green hover:bg-kumia-green-dark text-black',
  },
};

const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  warning = null,
  confirmLabel = 'Confirmar',
  confirmIcon: ConfirmIcon = null,
  cancelLabel = 'Cancelar',
  variant = 'danger',
  loading = false,
}) => {
  if (!isOpen) return null;

  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.danger;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 overflow-y-auto"
      onClick={loading ? undefined : onClose}
    >
      <div
        className="bg-surface-1 border border-line rounded-xl max-w-md w-full max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header con ícono circular */}
        <div className="p-6 text-center">
          <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${styles.iconBg}`}>
            <AlertTriangle className={`w-8 h-8 ${styles.iconColor}`} />
          </div>
          <h3 className="text-xl font-semibold text-content-primary mb-2">{title}</h3>
          {description && (
            <div className="text-content-muted text-sm">{description}</div>
          )}
        </div>

        {/* Advertencia opcional */}
        {warning && (
          <div className="px-6 pb-4">
            <div className="bg-red-50 dark:bg-red-900/10 border border-red-500/20 rounded-lg p-3">
              <div className="text-red-700 dark:text-red-400 text-xs flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>{warning}</span>
              </div>
            </div>
          </div>
        )}

        {/* Botones */}
        <div className="flex items-center gap-3 p-6 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-3 bg-surface-4 hover:bg-surface-4 text-content-primary rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50 ${styles.confirmBtn}`}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Procesando...
              </>
            ) : (
              <>
                {ConfirmIcon && <ConfirmIcon className="w-4 h-4" />}
                {confirmLabel}
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ConfirmDialog;
